import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Typography, Button, Space, Descriptions, Row, Col, Tag, Spin, Tabs, message, Empty } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApartmentUnitById, deleteApartmentUnit, updateApartmentUnit } from '@/api/properties';
import { getResidentById } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { ApartmentUnitFormModal } from '@/components/features/resources/properties/ApartmentUnitFormModal';
import type { ApartmentUnit } from '@/types/resources/property';

import dayjs from 'dayjs';

const { Title } = Typography;

export const ApartmentUnitDetails: React.FC = () => {
  const { t } = useTranslation(['properties', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; unitNumber?: string } | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const state = location.state as { apartmentUnit: ApartmentUnit } | null;

  // Get unit data - only fetch if we don't have state data
  const { data: unit, isLoading } = useQuery({
    queryKey: ['apartmentUnit', id, firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getApartmentUnitById(id!, firestoreUser);
    },
    enabled: !!id && !!firestoreUser && !state?.apartmentUnit, // Only fetch if no state data
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use state data if available, otherwise use fetched data
  const displayUnit = state?.apartmentUnit || unit;

  // Fetch resident data only when resident tab is active
  const { data: resident, isLoading: residentLoading } = useQuery({
    queryKey: ['resident', displayUnit?.resident?.id],
    queryFn: () => {
      if (!displayUnit?.resident?.id || !firestoreUser) return null;
      return getResidentById(displayUnit.resident.id, firestoreUser);
    },
    enabled: activeTab === 'resident' && !!displayUnit?.resident?.id && !!firestoreUser
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ApartmentUnit> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateApartmentUnit(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:apartmentUnits.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['apartmentUnits', id] });
      setIsModalVisible(false);
    },
    onError: (error: Error) => {
      message.error(t('properties:apartmentUnits.messages.updateError'));
      console.error('Error updating apartment unit:', error);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteApartmentUnit(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:apartmentUnits.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['apartmentUnits'] });
      navigate('/resources/properties/units');
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error as any);
      } else {
        message.error(t('properties:apartmentUnits.messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting apartment unit:', error);
    }
  });

  const handleEdit = () => {
    setIsModalVisible(true);
  };

  const handleModalSubmit = (values: Partial<ApartmentUnit>) => {
    if (displayUnit?.id) {
      updateMutation.mutate({ id: displayUnit.id, data: values });
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  if (isLoading && !state?.apartmentUnit) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayUnit) {
    return <Card>Unit not found</Card>;
  }

  const tabItems = [
    {
      key: 'overview',
      label: t('common:details'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('properties:fields.number')}>
              {displayUnit.number}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.floor')}>
              {displayUnit.floor?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.block')}>
              {displayUnit.block?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.bedrooms')}>
              {displayUnit.bedrooms || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.bathrooms')}>
              {displayUnit.bathrooms || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.area')}>
              {displayUnit.area ? `${displayUnit.area} sq.ft` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.status')}>
              <Tag color={displayUnit.status === 'occupied' ? 'green' : displayUnit.status === 'maintenance' ? 'orange' : 'blue'}>
                {t(`common:status.${displayUnit.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.description')}>
              {displayUnit.description || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'resident',
      label: t('properties:fields.resident'),
      children: (
        <Card>
          {residentLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Spin size="large" />
            </div>
          ) : resident ? (
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('residents:fields.user')}>
                {resident.user?.displayName || resident.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('residents:fields.type')}>
                {t(`residents:residencyTypes.${resident.type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('residents:fields.moveInDate')}>
                {resident.moveInDate ? dayjs(resident.moveInDate).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('residents:fields.status')}>
                {t(`residents:status.${resident.status}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('residents:fields.contacts')}>
                {resident.contacts?.length ? `${resident.contacts.length} contact(s)` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('residents:fields.notes')}>
                {resident.notes || '-'}
              </Descriptions.Item>
            </Descriptions>
          ) : displayUnit.resident ? (
            <Empty description={t('properties:residentNotFound')} />
          ) : (
            <Empty description={t('properties:noResident')} />
          )}
        </Card>
      ),
    },
    {
      key: 'activityLog',
      label: t('common:activityLog'),
      children: (
        <Card>
          <ActivityLogList activityRecords={Array.isArray(displayUnit.activityRecords) ? displayUnit.activityRecords : []} />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/resources/properties/units')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayUnit.number}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => setShowDeleteModal(true)}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs 
        items={tabItems} 
        onChange={handleTabChange}
        activeKey={activeTab}
      />

      <ApartmentUnitFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleModalSubmit}
        initialValues={displayUnit}
        title={t('properties:apartmentUnits.actions.edit')}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleDelete}
        title={t('properties:apartmentUnits.messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
      />
    </div>
  );
}; 